import React, { useState } from "react";
import { Truck, FileText, Wrench, Settings, X, Menu, Users, LogOut, Bell, ChevronRight, ClipboardCheck, Package, LifeBuoy } from "lucide-react";
import { UserProfileModal } from "./UserProfileModal";
import { canAccessNav, hasPermission, PERMISSIONS } from "@/utils/permissions";
import { useT } from "@/i18n/LanguageContext";

export const Sidebar = ({ activeSection, setActiveSection, isOpen, onClose, currentUser, onLogout, pendingRequestsCount, activeServiceRequestsCount, newPartsRequestsCount, onUserUpdate }) => {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const { t } = useT();
  const userRole = currentUser?.role;
  
  // Define all nav items with their permission requirements
  const allNavItems = [
    { id: "assets", label: "Assets", icon: Truck },
    { id: "dvir", label: "DVIR", icon: ClipboardCheck },
    { id: "service-request", label: "Service Request", icon: Wrench, badge: activeServiceRequestsCount },
    { id: "parts", label: "Parts", icon: Package, badge: newPartsRequestsCount },
    { id: "reports", label: "Reports", icon: FileText },
    { id: "settings", label: "Settings", icon: Settings },
    { id: "users", label: "Users", icon: Users, badge: pendingRequestsCount, requiresPermission: PERMISSIONS.NAV.USERS },
    { id: "help", label: "Help Center", icon: LifeBuoy },
  ];

  // Filter nav items based on user permissions
  const navItems = allNavItems.filter(item => {
    if (item.requiresPermission) {
      return hasPermission(userRole, item.requiresPermission);
    }
    return canAccessNav(userRole, item.id);
  });

  const handleNavClick = (id) => {
    setActiveSection(id);
    if (onClose) onClose();
  };

  // Get avatar crop position
  const cropPosition = currentUser?.photo_crop || { x: 50, y: 50, zoom: 1 };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[140] lg:hidden"
          onClick={onClose}
        />
      )}
      
      <div 
        className={`sidebar ${isOpen ? 'sidebar-open' : ''}`} 
        data-testid="sidebar"
      >
        <div className="sidebar-header">
          <div className="flex items-center justify-between">
            <div className="flex-1 text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-tracker-162/artifacts/n3an3dwp_djlj7u57_PEW.jpeg" 
                alt="Pacific Expressway" 
                className="h-14 w-auto object-contain mx-auto"
              />
              <h1 className="text-base font-bold text-slate-900 mt-2">Pacific Expressway</h1>
              <p className="text-xs text-slate-500">{t("Fleet Maintenance")}</p>
              <p className="text-[10px] text-slate-400 mt-1">Version 2.0</p>
            </div>
            <button 
              className="lg:hidden p-3 hover:bg-slate-100 rounded-full absolute right-2 top-2 touch-manipulation"
              onClick={onClose}
            >
              <X className="w-6 h-6 text-slate-500" />
            </button>
          </div>
        </div>
        
        <nav className="sidebar-nav px-3 flex-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => handleNavClick(item.id)}
              className={`nav-item w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left mb-1 relative ${
                activeSection === item.id ? "active" : "text-slate-600"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{t(item.label)}</span>
              {item.badge > 0 && (
                <span className="absolute right-3 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* User Info & Logout - with safe area padding for iOS */}
        {currentUser && (
          <div className="p-3 border-t border-slate-200" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            {/* Clickable User Profile Section */}
            <button
              onClick={() => setShowProfileModal(true)}
              className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg mb-2 transition-colors text-left"
              data-testid="btn-edit-profile"
            >
              {currentUser.photo ? (
                <div 
                  className="w-10 h-10 rounded-full flex-shrink-0"
                  style={{
                    backgroundImage: `url(${currentUser.photo})`,
                    backgroundSize: `${(cropPosition.zoom || 1) * 100}%`,
                    backgroundPosition: `${cropPosition.x}% ${cropPosition.y}%`,
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold">{currentUser.first_name?.[0]}{currentUser.last_name?.[0]}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 text-sm truncate">{currentUser.first_name} {currentUser.last_name}</div>
                <div className="text-xs text-slate-500 capitalize">{currentUser.role?.replace('_', ' ')}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </button>
            
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              data-testid="btn-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">{t("Sign Out")}</span>
            </button>
          </div>
        )}
      </div>

      {/* Profile Edit Modal */}
      <UserProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        currentUser={currentUser}
        onUpdate={() => {
          setShowProfileModal(false);
          if (onUserUpdate) onUserUpdate();
        }}
      />
    </>
  );
};

export const MobileHeader = ({ onMenuClick, title, pendingRequestsCount }) => (
  <div className="mobile-header lg:hidden">
    <button 
      onClick={onMenuClick}
      className="p-2 hover:bg-slate-100 rounded-lg relative"
      data-testid="btn-mobile-menu"
    >
      <Menu className="w-6 h-6 text-slate-700" />
      {pendingRequestsCount > 0 && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
          {pendingRequestsCount}
        </span>
      )}
    </button>
    <img 
      src="https://customer-assets.emergentagent.com/job_vehicle-tracker-162/artifacts/n3an3dwp_djlj7u57_PEW.jpeg" 
      alt="Pacific Expressway" 
      className="h-10 w-auto object-contain"
    />
    <div className="w-10" /> {/* Spacer for centering */}
  </div>
);
